/**
 * Configuracion del panel, resuelta y validada una sola vez al arrancar.
 *
 * Politica de fallo: **fallar temprano y visible**. Si falta una variable, la
 * app muestra una pantalla que dice cual falta, en vez de arrancar y morir mas
 * tarde con un 401 o un fetch a "undefined/...". Un panel interno mal
 * configurado tiene que ser obvio para quien lo despliega.
 *
 * `VITE_API_BASE_URL` vacio NO es un error: significa "mismo origen", que es lo
 * que usa `npm run dev` a traves del proxy declarado en `vite.config.ts`.
 */

export interface AppConfig {
  /** Base de la API, sin barra final. Cadena vacia = mismo origen. */
  readonly apiBaseUrl: string;
  /** URL del proyecto de Supabase. Debe ser la misma que valida el backend. */
  readonly supabaseUrl: string;
  /** Anon key publica del proyecto de Supabase. */
  readonly supabaseAnonKey: string;
}

/** Prefijo de la API, espejo de `settings.API_V1_PREFIX` en el backend. */
export const API_V1_PREFIX = "/api/v1";

export class ConfigError extends Error {
  readonly missing: readonly string[];

  constructor(missing: readonly string[]) {
    super(`Faltan variables de entorno: ${missing.join(", ")}`);
    this.name = "ConfigError";
    this.missing = missing;
  }
}

function trimTrailingSlash(value: string): string {
  return value.endsWith("/") ? value.replace(/\/+$/, "") : value;
}

/**
 * Leer y validar la configuracion.
 *
 * @throws {ConfigError} Si falta alguna variable obligatoria.
 */
export function loadConfig(): AppConfig {
  const env = import.meta.env;
  const missing: string[] = [];

  const supabaseUrl = env.VITE_SUPABASE_URL?.trim() ?? "";
  if (!supabaseUrl) missing.push("VITE_SUPABASE_URL");

  const supabaseAnonKey = env.VITE_SUPABASE_ANON_KEY?.trim() ?? "";
  if (!supabaseAnonKey) missing.push("VITE_SUPABASE_ANON_KEY");

  if (missing.length > 0) {
    throw new ConfigError(missing);
  }

  return {
    // Vacio a proposito cuando no esta definida: mismo origen (proxy de dev).
    apiBaseUrl: trimTrailingSlash(env.VITE_API_BASE_URL?.trim() ?? ""),
    supabaseUrl: trimTrailingSlash(supabaseUrl),
    supabaseAnonKey,
  };
}
